/**
 * Scroll-direction based header show/hide.
 * Imperative DOM manipulation — no JSX needed.
 *
 * Mobile only (≤640px): entire nav hides on scroll-down, shows on scroll-up.
 * Desktop: nav is always visible (Pattern C).
 */
export function initScrollHeader(): void {
  const nav = document.querySelector<HTMLElement>('[data-main-nav]');
  if (!nav) return;

  const HIDE_CLASS = 'nav-hidden';
  const THRESHOLD = 8; // px — ignore micro-scrolls
  const MQ = window.matchMedia('(max-width: 640px)');

  let prevY = window.scrollY;
  let ticking = false;
  let active = MQ.matches; // only active on mobile

  // Track header height via CSS variable for dropdown positioning
  const updateHeaderHeight = () => {
    const h = nav.offsetHeight;
    document.documentElement.style.setProperty('--header-h', `${h}px`);
  };

  const ro = new ResizeObserver(() => updateHeaderHeight());
  ro.observe(nav);
  updateHeaderHeight();

  const shouldStayVisible = (): boolean => {
    // Don't hide while mobile search overlay is open
    if (document.body.classList.contains('search-open')) return true;
    // Don't hide while search dropdown is expanded
    const input = document.getElementById('search-input');
    if (input?.getAttribute('aria-expanded') === 'true') return true;
    return false;
  };

  const onScroll = () => {
    if (!active || ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      const currY = window.scrollY;
      const delta = currY - prevY;

      // Always show at top (iOS overscroll guard)
      if (currY <= 0) {
        nav.classList.remove(HIDE_CLASS);
      } else if (shouldStayVisible()) {
        nav.classList.remove(HIDE_CLASS);
      } else if (delta > THRESHOLD) {
        // Scrolling down → hide nav
        nav.classList.add(HIDE_CLASS);
      } else if (delta < -THRESHOLD) {
        // Scrolling up → show nav
        nav.classList.remove(HIDE_CLASS);
      }

      prevY = currY;
      ticking = false;
    });
  };

  window.addEventListener('scroll', onScroll, { passive: true });

  // Respond to viewport changes (rotate, resize)
  const onMediaChange = (e: MediaQueryListEvent | MediaQueryList) => {
    active = e.matches;
    if (!active) {
      // Switched to desktop → ensure nav is visible
      nav.classList.remove(HIDE_CLASS);
    }
  };
  MQ.addEventListener('change', onMediaChange);

  // Reset on page navigation (BFCache / View Transitions)
  const reset = () => {
    nav.classList.remove(HIDE_CLASS);
    prevY = window.scrollY;
  };
  window.addEventListener('pageshow', reset);
  // pagereveal is supported in Chromium for View Transitions
  window.addEventListener('pagereveal' as string, reset);

  // Re-measure on orientation change / resize
  window.addEventListener('orientationchange', updateHeaderHeight);
  window.addEventListener('resize', updateHeaderHeight);
}
